import { useCallback, useMemo } from 'react';
import { ClassifierData, ClassifierText } from 'types/classifiers';
import { CustomField, CustomFieldDto, CustomFieldType, SectionedCustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { MinDto } from 'types/other';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useCustomFieldsList } from 'utils/useCustomFieldsList';
import { CUSTOM_FIELD_PREFIX } from './customFieldPrefix';

export { CUSTOM_FIELD_PREFIX };

type CustomFieldsHookProps = {
    entityType: EntityType | null;
    fields?: CustomField[];
    // A contact custom field can be mandatory for some relationship types only. The form passes the
    // currently selected code, so the requirement follows the dropdown instead of the stored value.
    relationshipTypeCode?: string | null;
};

const DEFAULT_FIELDS: CustomField[] = [];

/** Whether a custom field's definition makes it mandatory for a contact of the relationship type. */
export function isMandatoryFor(definition: CustomField, relationshipTypeCode?: string | null): boolean {
    const mandatoryFor = definition.mandatoryRelationshipTypeCodes;
    return definition.isMandatory && (!mandatoryFor?.length || mandatoryFor.includes(relationshipTypeCode ?? ''));
}

export const useCustomFields = (props: CustomFieldsHookProps) => {
    const { entityType, fields = DEFAULT_FIELDS, relationshipTypeCode } = props;

    const { customFieldsArray: customFieldsEmpty, isLoading: isLoadingCustomFieldList } = useCustomFieldsList(
        entityType ?? ''
    );
    const { allClassifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: [] });

    const sortedFields = useMemo<CustomField[]>(() => {
        return customFieldsEmpty.map((emptyField) => {
            const existingField = fields.find((field) => field.id === emptyField.id);
            const field = existingField ? existingField : emptyField;
            if (!emptyField.mandatoryRelationshipTypeCodes?.length) {
                return field;
            }
            // The definition decides, not `field`: a caller can feed an already narrowed list back in
            // as `fields` (the contact edit page does), and reading its `isMandatory` would latch the
            // requirement off for good.
            return { ...field, isMandatory: isMandatoryFor(emptyField, relationshipTypeCode) };
        });
    }, [fields, customFieldsEmpty, relationshipTypeCode]);

    const filteredSortedFields = useMemo<CustomField[]>(() => {
        return sortedFields.filter((field) => field.fieldType !== CustomFieldType.HEADER);
    }, [sortedFields]);

    const initialCustomFieldsObject = useMemo(
        () => customFieldFormValues(filteredSortedFields, allClassifiers),
        [filteredSortedFields, allClassifiers]
    );

    // `initialCustomFieldsObject` for a record's custom fields as just fetched rather than as the hook was given
    // them, so a section save builds the record it sends on what is stored now.
    const customFieldValuesOf = useCallback(
        (stored: CustomField[] = DEFAULT_FIELDS): Record<string, unknown> =>
            Object.fromEntries(
                filteredSortedFields.map((field) => {
                    const current =
                        stored.find(({ id }) => id === field.id) ??
                        customFieldsEmpty.find(({ id }) => id === field.id) ??
                        field;
                    const value = current.isMultivalue ? current.values : current.value;
                    return [CUSTOM_FIELD_PREFIX + field.id, convertValueForCustomInput(field, value, allClassifiers)];
                })
            ),
        [filteredSortedFields, customFieldsEmpty, allClassifiers]
    );

    // this takes the form values and maps them to valid json that is sent to the backend api endpoint.
    // Parameter is `Record<string, unknown>` because the function reads dynamically-registered
    // `customField_<id>` keys that RHF adds at runtime; concrete form types (MatterForm, ContactForm,
    // etc.) don't declare them statically. Callers cast at the call site to acknowledge the gap.
    const mapCustomFieldsToDto = useCallback(
        (formData: Record<string, unknown>): CustomFieldDto[] => {
            const mappedResult: CustomFieldDto[] = filteredSortedFields.map((field) => {
                const internalFieldName = String(CUSTOM_FIELD_PREFIX + field.id);
                let value: unknown = null;

                switch (field.fieldType) {
                    case CustomFieldType.CLASSIFIER: {
                        const formValue = field.isMultivalue
                            ? (formData[internalFieldName] as ClassifierText[])
                            : (formData[internalFieldName] as ClassifierText);
                        value = Array.isArray(formValue)
                            ? formValue.map((classifier) => classifier?.value)
                            : formValue?.value;
                        break;
                    }
                    case CustomFieldType.DATE: {
                        const formValue = formData[internalFieldName] as string | null;
                        value = formValue ? new Date(formValue) : null;
                        break;
                    }
                    case CustomFieldType.EMPLOYEE: {
                        const formValue = field.isMultivalue
                            ? (formData[internalFieldName] as MinDto[])
                            : (formData[internalFieldName] as MinDto);
                        value = formValue
                            ? Array.isArray(formValue)
                                ? formValue.map((employee) => employee.id)
                                : formValue.id
                            : null;
                        break;
                    }
                    case CustomFieldType.BOOLEAN: {
                        value = (formData[internalFieldName] as boolean | null) || false;
                        break;
                    }
                    case CustomFieldType.DECIMAL:
                    case CustomFieldType.INTEGER:
                    case CustomFieldType.TEXTAREA:
                    case CustomFieldType.TEXTINPUT:
                    default: {
                        value = (formData[internalFieldName] as string | null) || null;
                        break;
                    }
                }
                if (field.isMultivalue) {
                    return {
                        id: field.id,
                        isMandatory: field.isMandatory,
                        isMultivalue: true,
                        fieldType: field.fieldType,
                        values: value,
                    } as CustomFieldDto;
                } else {
                    return {
                        id: field.id,
                        isMandatory: field.isMandatory,
                        isMultivalue: false,
                        fieldType: field.fieldType,
                        value: value,
                    } as CustomFieldDto;
                }
            });
            return mappedResult;
        },
        [filteredSortedFields]
    );

    return {
        customFields: sortedFields,
        customFieldsFiltered: filteredSortedFields,
        initialCustomFieldsObject,
        customFieldValuesOf,
        mapCustomFieldsToDto,
        isLoading: isLoadingClassifiers || isLoadingCustomFieldList,
        customFieldsEmpty,
    };
};

/** The fields' values as the form inputs take them, keyed `customField_<id>`. */
export const customFieldFormValues = (
    fields: CustomField[],
    allClassifiers: ClassifierData[]
): Record<string, unknown> =>
    Object.fromEntries(
        fields.map((field) => [
            CUSTOM_FIELD_PREFIX + field.id,
            convertValueForCustomInput(field, field.isMultivalue ? field.values : field.value, allClassifiers),
        ])
    );

const sortFieldsByOrderNum = (fields: CustomField[]): CustomField[] => {
    return (fields || []).sort((a, b) => a.orderNum - b.orderNum);
};

export const groupFieldsByHeaders = (fields: CustomField[]): SectionedCustomField[] => {
    if (fields.length === 0) {
        return [];
    }

    const result: SectionedCustomField[] = [];
    let currentGroup: SectionedCustomField = { id: 0, section: 'Custom fields', fields: [], shouldTranslate: false }; // set default form section name as it is mandatory to have one

    sortFieldsByOrderNum(fields).forEach((field) => {
        if (field.fieldType === CustomFieldType.HEADER) {
            if (currentGroup.fields.length > 0) {
                result.push(currentGroup);
            }
            currentGroup = {
                id: field.id,
                section: field.label ?? '',
                fields: [],
                shouldTranslate: field.shouldTranslate,
            };
        } else {
            currentGroup.fields.push(field);
        }
    });

    if (currentGroup.fields.length > 0) {
        result.push(currentGroup);
    }

    return result;
};

// This takes the field returned from backed, as well as its value and maps it to values that the inputs expect
const convertValueForCustomInput = (field: CustomField, value: unknown, allClassifiers: ClassifierData[]): unknown => {
    switch (field.fieldType) {
        case CustomFieldType.CLASSIFIER: {
            const stringValue = field.isMultivalue ? (value ?? []) : (value ?? null);
            const classifiers = allClassifiers.find((classifier) => classifier.code === field.classifierCode);
            const classifierValues =
                getOptionsFromClassifier<string, string | number>(classifiers).filter((classifier) =>
                    Array.isArray(stringValue)
                        ? stringValue.includes(classifier.value)
                        : classifier.value === stringValue
                ) ?? [];
            return (field.isMultivalue ? classifierValues : classifierValues[0]) || null;
        }
        case CustomFieldType.DATE:
            return value ? formatDateStringInput(new Date(value as string | number | Date)) : '';
        case CustomFieldType.DECIMAL:
            return value ?? '';
        case CustomFieldType.EMPLOYEE:
            return field.isMultivalue ? (value ?? []) : (value ?? null);
        case CustomFieldType.INTEGER:
            return value ?? '';
        case CustomFieldType.TEXTAREA:
            return value ?? '';
        case CustomFieldType.TEXTINPUT:
            return value ?? '';
        case CustomFieldType.BOOLEAN:
            return value ?? false;
        default:
            return value;
    }
};
